import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  DatePicker,
  Division,
  Drawer,
  EmptyState,
  Flex,
  Form,
  Heading,
  Icon,
  Icons,
  Input,
  Paragraph,
  Row,
  Select,
  type SelectOption,
  Span,
  Table,
  type TableColumnType,
  Textarea,
  useToast,
} from "@ir/ui";
import { ProductLookUp } from "@/core/entities/lookup/product";
import { ICreatePromoProgramParams } from "@/core/repositories/promotion/IPromoDetail.repository";
import { useLookup, useWorkspace } from "@/hooks";
import { useCreatePromoProgram, usePromotions } from "../../hooks";
import { useProgramDetailsOptions } from "../edit/hooks/useProgramDetailsOptions";
import EditProgramDetailsCol from "../edit/EditProgramDetailsCol";
import { PromoStatusBadge } from "../PromoStatusBadge";
import "../../styles/TradePromotion.scss";
import { getTruncatedText } from "@/core/utils";

const FUNDING_METHOD_OPTIONS: SelectOption[] = [
  { label: "Fixed amount", value: "FIXED_AMOUNT" },
  { label: "Percent amount", value: "PERCENT_OF_NET_SALES" },
];

interface SelectedProductRow {
  productId: number;
  upcItemNumber: string;
  itemName: string;
  categoryName: string;
  fundingMethod: string | null;
  fundingValue: string;
}

interface CreateProgramDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const formatProductOptionLabel = (product: ProductLookUp): string => {
  const upc = product.upcItemNumber || "—";
  const name = product.itemName || "—";
  const category = product.categoryName || "—";
  return `${upc} · ${name} · ${category}`;
};

const CreateProgramDrawer = ({ isOpen, onClose }: CreateProgramDrawerProps) => {
  const [form] = Form.useForm();
  const toast = useToast();
  const { settings } = useWorkspace();
  const { getPromotions } = usePromotions();
  const { isCreating, createPromoProgram } = useCreatePromoProgram();
  const {
    purchaserOptions,
    promoTypeOptions,
    retailerOptions,
    isPurchasersLoading,
    isPromoTypesLoading,
    isRetailersLoading,
  } = useProgramDetailsOptions();
  const { productsLookup, getProductsLookup } = useLookup();

  const [selectedProducts, setSelectedProducts] = useState<SelectedProductRow[]>([]);
  const [productPickerKey, setProductPickerKey] = useState(0);
  const [hasDateRangeError, setHasDateRangeError] = useState(false);

  const resetForm = useCallback(() => {
    form.resetFields();
    setSelectedProducts([]);
    setProductPickerKey(key => key + 1);
    setHasDateRangeError(false);
  }, [form]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    resetForm();
    void getProductsLookup({
      search: null,
      page_number: 1,
      page_size: 100,
    });
  }, [isOpen, getProductsLookup, resetForm]);

  useEffect(() => {
    if (selectedProducts.length === 0) {
      form.setFieldValue("products", undefined);
      return;
    }

    form.setFields([
      {
        name: "products",
        value: selectedProducts,
        errors: [],
      },
    ]);
  }, [form, selectedProducts]);

  const selectedProductIds = useMemo(
    () => new Set(selectedProducts.map(product => product.productId)),
    [selectedProducts]
  );

  const productOptions = useMemo<SelectOption[]>(() => {
    return (productsLookup.data ?? [])
      .filter(product => !selectedProductIds.has(product.productId))
      .map(product => ({
        value: product.productId.toString(),
        label: formatProductOptionLabel(product),
      }));
  }, [productsLookup.data, selectedProductIds]);

  const productsById = useMemo(() => {
    const map = new Map<number, ProductLookUp>();
    for (const product of productsLookup.data ?? []) {
      map.set(product.productId, product);
    }
    return map;
  }, [productsLookup.data]);

  const handleReset = useCallback(() => {
    if (isCreating) {
      return;
    }
    resetForm();
  }, [isCreating, resetForm]);

  const handleClose = useCallback(() => {
    if (isCreating) {
      return;
    }
    resetForm();
    onClose();
  }, [isCreating, onClose, resetForm]);

  const handleAddProduct = useCallback(
    (option: SelectOption | null) => {
      if (!option) {
        return;
      }
      const productId = Number.parseInt(option.value, 10);
      if (Number.isNaN(productId) || selectedProductIds.has(productId)) {
        return;
      }
      const product = productsById.get(productId);
      if (!product) {
        return;
      }
      setSelectedProducts(prev => [
        ...prev,
        {
          productId: product.productId,
          upcItemNumber: product.upcItemNumber ?? "—",
          itemName: product.itemName,
          categoryName: product.categoryName ?? "—",
          fundingMethod: null,
          fundingValue: "",
        },
      ]);
      setProductPickerKey(key => key + 1);
    },
    [productsById, selectedProductIds]
  );

  const handleRemoveProduct = useCallback((productId: number) => {
    setSelectedProducts(prev => prev.filter(product => product.productId !== productId));
  }, []);

  const handleFundingMethodChange = useCallback((productId: number, option: SelectOption | null) => {
    setSelectedProducts(prev =>
      prev.map(product =>
        product.productId === productId ? { ...product, fundingMethod: option?.value ?? null } : product
      )
    );
  }, []);

  const handleFundingValueChange = useCallback((productId: number, value: string) => {
    setSelectedProducts(prev =>
      prev.map(product => (product.productId === productId ? { ...product, fundingValue: value } : product))
    );
  }, []);

  const productColumns = useMemo<TableColumnType<SelectedProductRow>[]>(
    () => [
      {
        title: "UPC/Item Number",
        dataIndex: "upcItemNumber",
        key: "upcItemNumber",
        width: 140,
      },
      {
        title: "Name",
        dataIndex: "itemName",
        key: "itemName",
        cellRender: (_value, record) => (
          <Span className="text-grey-500 text-sm d-block" title={record.itemName}>
            {getTruncatedText(record.itemName, 30)}
          </Span>
        ),
      },
      {
        title: "Category",
        dataIndex: "categoryName",
        key: "categoryName",
        width: 140,
        cellRender: (_value, record) => (
          <Span className="text-grey-500 text-sm d-block" title={record.categoryName}>
            {getTruncatedText(record.categoryName, 25)}
          </Span>
        ),
      },
      {
        title: "Funding Method",
        dataIndex: "fundingMethod",
        key: "fundingMethod",
        width: 150,
        cellRender: (_value, record) => (
          <Select
            key={`${record.productId}-${record.fundingMethod ?? "none"}`}
            size="small"
            options={FUNDING_METHOD_OPTIONS}
            isClearable={false}
            defaultValue={
              record.fundingMethod
                ? FUNDING_METHOD_OPTIONS.find(option => option.value === record.fundingMethod)
                : FUNDING_METHOD_OPTIONS[1]
            }
            onChange={option => handleFundingMethodChange(record.productId, option as SelectOption | null)}
            placeholder="Select"
          />
        ),
      },
      {
        title: "Funding Value",
        dataIndex: "fundingValue",
        key: "fundingValue",
        width: 100,
        cellRender: (_value, record) => (
          <Input
            size="small"
            type="number"
            className="no-helper-input bg-white"
            value={record.fundingValue}
            onChange={event => handleFundingValueChange(record.productId, event.target.value)}
            placeholder="0"
          />
        ),
      },
      {
        title: "",
        dataIndex: "productId",
        key: "actions",
        width: 56,
        cellRender: (_value, record) => (
          <Button
            size="small"
            variant="clear"
            color="grey"
            aria-label="Remove product"
            onPress={() => handleRemoveProduct(record.productId)}
            isDisabled={isCreating}
          >
            <Icon name={Icons.Trash} size="small" />
          </Button>
        ),
      },
    ],
    [handleFundingMethodChange, handleFundingValueChange, handleRemoveProduct, isCreating]
  );

  const handleSubmit = async () => {
    const values = await form.validateFields().catch(() => undefined);
    if (!values) {
      return;
    }

    if (selectedProducts.length === 0) {
      toast.error({ content: "Add at least one product" });
      return;
    }

    const incompleteProduct = selectedProducts.find(
      product => !product.fundingMethod || product.fundingValue.trim() === ""
    );
    if (incompleteProduct) {
      toast.error({ content: "Each product requires a funding method and funding value" });
      return;
    }

    const params: ICreatePromoProgramParams = {
      name: values.name.trim(),
      promoTypeId: Number.parseInt(values.promoType.value, 10),
      purchaserId: Number.parseInt(values.purchaser.value, 10),
      retailerId: Number.parseInt(values.retailer.value, 10),
      startDate: values.startDate,
      endDate: values.endDate,
      notes: values.notes && values.notes.length > 0 ? values.notes : null,
      committedBudget: values.committedBudget ? Number.parseFloat(values.committedBudget) : null,
      products: selectedProducts.map(product => ({
        productId: product.productId,
        fundingMethod: product.fundingMethod as string,
        fundingValue: Number.parseFloat(product.fundingValue),
      })),
    };

    try {
      await createPromoProgram(params);
      toast.success({ content: "Promotion created" });
      void getPromotions({ isLoading: false, isRefreshing: true });
      resetForm();
      onClose();
    } catch (error) {
      toast.error({
        content: error instanceof Error ? error.message : "Failed to create promotion",
      });
    }
  };

  const startDate = Form.useWatch("startDate", form);
  const endDate = Form.useWatch("endDate", form);
  const headerTitle = "Create Promotion";

  const renderHeader = () => (
    <Flex
      className="create-program-drawer-header bg-white w-100 border-bottom-1 border-color-grey-200"
      direction="column"
    >
      <Flex direction="row" align="start" justifyContent="space-between" wrap="no-wrap" className="px-4 py-3 w-100">
        <Flex direction="column" className="min-w-0" style={{ width: "calc(100% - 45px)" }}>
          <Heading level={6} className="text-grey-700 font-bold m-0" htmlTitle={headerTitle}>
            {headerTitle} <PromoStatusBadge statusName="Draft" statusKey="DRAFT" />
          </Heading>
          <Span className="text-grey-500 text-xs mt-1">
            Enter promotion details and products, then submit to save as Draft for later review and activation.
          </Span>
        </Flex>
        <Flex align="center" justifyContent="end" wrap="no-wrap">
          <Button
            size="medium"
            variant="clear"
            color="grey"
            aria-label="Close"
            onPress={handleClose}
            isDisabled={isCreating}
          >
            <Icon name={Icons.Close} size="medium" />
          </Button>
        </Flex>
      </Flex>

      <Flex align="center" justifyContent="space-between" className="px-4 pb-3 create-program-drawer-actions w-100">
        <Button
          size="small"
          color="grey"
          variant="outline"
          className="m-0"
          onPress={handleReset}
          isDisabled={isCreating}
        >
          Reset
        </Button>
        <Button size="small" variant="solid" color="primary" isLoading={isCreating} onPress={() => void handleSubmit()}>
          Submit
        </Button>
      </Flex>
    </Flex>
  );

  return (
    <Drawer
      key="create-program-drawer"
      className="create-program-drawer"
      isOpen={isOpen}
      mask={true}
      size="large"
      isDismissable={false}
      isKeyboardDismissDisabled={true}
      header={renderHeader()}
    >
      <Form
        form={form}
        className="create-program-form p-4"
        onFieldsChange={(_changedFields, allFields) => {
          const hasError = allFields.some(field => {
            const fieldName = Array.isArray(field.name) ? field.name[0] : field.name;
            return (fieldName === "startDate" || fieldName === "endDate") && Boolean(field.errors?.length);
          });
          setHasDateRangeError(current => (current === hasError ? current : hasError));
        }}
      >
        <Row>
          <EditProgramDetailsCol md={12}>
            <Form.Item
              name="name"
              label="Promotion Name"
              rules={[
                { required: true, message: "Promotion name is required" },
                { max: 100, message: "Promotion name cannot exceed 100 characters" },
              ]}
            >
              <Input size="small" placeholder="Enter promotion name" className="bg-white" maxLength={100} />
            </Form.Item>
          </EditProgramDetailsCol>

          <EditProgramDetailsCol md={4}>
            <Form.Item
              name="promoType"
              isList
              label="Promotion Type"
              rules={[{ required: true, message: "Promotion type is required" }]}
            >
              <Select size="small" options={promoTypeOptions} isClearable={false} isLoading={isPromoTypesLoading} />
            </Form.Item>
          </EditProgramDetailsCol>

          <EditProgramDetailsCol md={4}>
            <Form.Item
              name="purchaser"
              isList
              label="Purchaser"
              rules={[{ required: true, message: "Purchaser is required" }]}
            >
              <Select size="small" options={purchaserOptions} isClearable={false} isLoading={isPurchasersLoading} />
            </Form.Item>
          </EditProgramDetailsCol>

          <EditProgramDetailsCol md={4}>
            <Form.Item
              name="retailer"
              isList
              label="Retailer"
              rules={[{ required: true, message: "Retailer is required" }]}
            >
              <Select size="small" options={retailerOptions} isClearable={false} isLoading={isRetailersLoading} />
            </Form.Item>
          </EditProgramDetailsCol>

          <EditProgramDetailsCol md={4} className="mb-3">
            <Division className="mb-2">
              <Span className="d-flex text-sm font-medium text-grey-700">
                Date Range: <sub className="form-item-required">*</sub>
              </Span>
            </Division>
            <Division className={`display-picker bg-white${hasDateRangeError ? " input-error" : ""}`}>
              <Division>
                <Form.Item
                  name="startDate"
                  label="Start date"
                  dependencies={["endDate"]}
                  rules={[
                    { required: true, message: "Start date is required" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const selectedEndDate = getFieldValue("endDate");
                        if (value && selectedEndDate && selectedEndDate < value) {
                          return Promise.reject(new Error("Start date must be on or before end date"));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    dateFormat={settings?.displayDateFormat}
                    shouldCloseOnInteractOutside={true}
                    isNonModal={true}
                    maxValue={endDate}
                  />
                </Form.Item>
              </Division>
              <Division>
                <Form.Item
                  name="endDate"
                  label="End date"
                  dependencies={["startDate"]}
                  rules={[
                    { required: true, message: "End date is required" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const selectedStartDate = getFieldValue("startDate");
                        if (value && selectedStartDate && value < selectedStartDate) {
                          return Promise.reject(new Error("End date must be on or after start date"));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    dateFormat={settings?.displayDateFormat}
                    minValue={startDate}
                    shouldCloseOnInteractOutside={true}
                    isNonModal={true}
                  />
                </Form.Item>
              </Division>
            </Division>
          </EditProgramDetailsCol>

          <EditProgramDetailsCol md={4}>
            <Form.Item name="committedBudget" label="Committed Budget ($)">
              <Input size="small" type="number" className="no-helper-input bg-white" />
            </Form.Item>
          </EditProgramDetailsCol>

          <EditProgramDetailsCol md={12}>
            <Form.Item name="notes" label="Notes">
              <Textarea size="small" maxLength={500} showCharCount />
            </Form.Item>
          </EditProgramDetailsCol>

          <EditProgramDetailsCol md={12}>
            <Form.Item
              name="products"
              label="Products"
              rules={[{ required: true, message: "At least one product is required" }]}
            >
              {(_control, meta) => (
                <Select
                  key={String(productPickerKey)}
                  size="small"
                  options={productOptions}
                  isClearable={false}
                  isSearchable={true}
                  isLoading={productsLookup.isLoading}
                  placeholder="Add a product"
                  isError={meta.errors.length > 0}
                  onChange={option => handleAddProduct(option as SelectOption | null)}
                />
              )}
            </Form.Item>
          </EditProgramDetailsCol>

          <EditProgramDetailsCol md={12} className="my-3">
            {selectedProducts.length === 0 ? (
              <EmptyState
                icon={Icons.Search}
                title={
                  <Heading level={6} className="text-grey-700 font-bold mt-3">
                    No products selected
                  </Heading>
                }
                description={
                  <Paragraph className="text-grey-500 text-sm">
                    Search in the product dropdown to add products to this promotion.
                  </Paragraph>
                }
                className="p-4 bg-white"
              />
            ) : (
              <Table
                columns={productColumns}
                dataSource={selectedProducts}
                border="row"
                aria-label="Selected products"
                sticky={true}
                scroll={{ x: "100%" }}
                className="mb-0"
              />
            )}
          </EditProgramDetailsCol>
        </Row>
      </Form>
    </Drawer>
  );
};

export { CreateProgramDrawer };
